import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useApi, Spinner, ErrBanner, today, fmtDate, fmtNum } from '../../hooks/useApi';
import { api } from '../../services/api';
import Can from '../../components/Can';

// Manual stock adjustment (shrinkage / gain / physical set). The backend records
// it in the Tank Ledger and re-derives current_stock from the ledger (single
// source of truth); when a rate is given the value change also posts to the
// financial ledger as an inventory loss/gain.
export default function StockAdjustment() {
  const { t: tr } = useTranslation();
  const [from, setFrom] = useState('');
  const [to, setTo] = useState(today());
  const [form, setForm] = useState({ adjustment_date: today(), tank_id: '', adjust_type: 'delta', qty: '', rate: '', reason: '' });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const notify = (m) => { setMsg(m); setTimeout(() => setMsg(''), 3500); };

  React.useEffect(() => {
    const wk = new Date(Date.now() - 30 * 86400 * 1000);
    setFrom(wk.toISOString().slice(0, 10));
  }, []);

  const { data: tanks } = useApi(() => api.stockLedger.tanks(), [], []);
  const { data, loading, error, reload } = useApi(
    () => (from && to ? api.stockAdjustments.list(from, to) : Promise.resolve([])),
    [from, to],
    []
  );

  const tankName = (id) => {
    const t = (tanks || []).find(x => String(x.id) === String(id));
    return t ? `${t.tank_no} (${t.product_code})` : id;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.tank_id) return notify('❌ ' + tr('stockAdjustment.errors.pickTank'));
    if (form.adjust_type === 'delta' && !(Number(form.qty) !== 0)) return notify('❌ ' + tr('stockAdjustment.errors.nonZeroChange'));
    if (form.adjust_type === 'set' && !(Number(form.qty) >= 0)) return notify('❌ ' + tr('stockAdjustment.errors.physicalStock'));
    setSaving(true);
    try {
      await api.stockAdjustments.create({
        adjustment_date: form.adjustment_date,
        tank_id: Number(form.tank_id),
        adjust_type: form.adjust_type,
        qty: Number(form.qty),
        rate: Number(form.rate || 0),
        reason: form.reason || null,
      });
      notify('✅ ' + tr('stockAdjustment.toasts.recorded'));
      setForm({ adjustment_date: today(), tank_id: '', adjust_type: 'delta', qty: '', rate: '', reason: '' });
      reload();
    } catch (err) { notify('❌ ' + err.message); }
    setSaving(false);
  };

  const remove = async (id) => {
    if (!window.confirm(tr('stockAdjustment.confirmDelete'))) return;
    try { await api.stockAdjustments.remove(id); notify('✅ ' + tr('common.deletedSuccessfully')); reload(); }
    catch (err) { notify('❌ ' + err.message); }
  };

  const qtyLabel = form.adjust_type === 'set' ? tr('stockAdjustment.physicalStockL') : tr('stockAdjustment.changeL');

  return (
    <div>
      <div className="page-header">
        <h2 className="page-title">{tr('stockAdjustment.title')}</h2>
        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
          {tr('stockAdjustment.subtitle')}
        </span>
      </div>

      {msg && <div style={{ marginBottom: 12, padding: '8px 12px', borderRadius: 6, background: 'var(--bg-muted, #f1f5f9)', color: 'var(--text-primary, #0f172a)', fontSize: 13 }}>{msg}</div>}
      {error && <ErrBanner msg={error} />}

      <Can module="stock_ledger" action="create">
        <form className="section-card" onSubmit={submit}
              style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 16 }}>
          <div className="form-group">
            <label>{tr('common.date')}</label>
            <input type="date" value={form.adjustment_date}
                   onChange={e => setForm({ ...form, adjustment_date: e.target.value })} required />
          </div>
          <div className="form-group">
            <label>{tr('common.tank')}</label>
            <select value={form.tank_id} onChange={e => setForm({ ...form, tank_id: e.target.value })} required>
              <option value="">{tr('stockAdjustment.selectPlaceholder')}</option>
              {(tanks || []).map(t => <option key={t.id} value={t.id}>{t.tank_no} ({t.product_code})</option>)}
            </select>
          </div>
          <div className="form-group">
            <label>{tr('common.type')}</label>
            <select value={form.adjust_type} onChange={e => setForm({ ...form, adjust_type: e.target.value })}>
              <option value="delta">{tr('stockAdjustment.typeDelta')}</option>
              <option value="set">{tr('stockAdjustment.typeSet')}</option>
            </select>
          </div>
          <div className="form-group">
            <label>{qtyLabel}</label>
            <input type="number" step="0.001" value={form.qty}
                   onChange={e => setForm({ ...form, qty: e.target.value })} required
                   placeholder={form.adjust_type === 'set' ? tr('stockAdjustment.qtyPlaceholderSet') : tr('stockAdjustment.qtyPlaceholderDelta')} />
          </div>
          <div className="form-group">
            <label>{tr('stockAdjustment.rateRsL')}</label>
            <input type="number" step="0.001" min="0" value={form.rate}
                   onChange={e => setForm({ ...form, rate: e.target.value })} placeholder={tr('stockAdjustment.ratePlaceholder')} />
          </div>
          <div className="form-group" style={{ flex: 1, minWidth: 160 }}>
            <label>{tr('common.reason')}</label>
            <input type="text" value={form.reason}
                   onChange={e => setForm({ ...form, reason: e.target.value })} placeholder={tr('stockAdjustment.reasonPlaceholder')} />
          </div>
          <button className="btn btn-primary" disabled={saving}>{saving ? tr('common.saving') + '…' : tr('stockAdjustment.addAdjustment')}</button>
        </form>
      </Can>

      <div className="section-card" style={{ display: 'flex', gap: 12, alignItems: 'flex-end', marginBottom: 12 }}>
        <div className="form-group"><label>{tr('common.from')}</label>
          <input type="date" value={from} onChange={e => setFrom(e.target.value)} /></div>
        <div className="form-group"><label>{tr('common.to')}</label>
          <input type="date" value={to} onChange={e => setTo(e.target.value)} /></div>
      </div>

      {loading ? <Spinner /> : (
        <div className="section-card" style={{ overflowX: 'auto' }}>
          <table className="data-table" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th>{tr('common.date')}</th><th>{tr('stockAdjustment.ref')}</th><th>{tr('common.tank')}</th><th>{tr('common.type')}</th>
                <th style={{ textAlign: 'right' }}>{tr('common.qty')}</th><th style={{ textAlign: 'right' }}>{tr('common.rate')}</th>
                <th>{tr('common.reason')}</th><th></th>
              </tr>
            </thead>
            <tbody>
              {(data || []).length === 0 && (
                <tr><td colSpan={8} style={{ textAlign: 'center', color: 'var(--text-muted)' }}>{tr('stockAdjustment.noRecords')}</td></tr>
              )}
              {(data || []).map(r => (
                <tr key={r.id}>
                  <td>{fmtDate(r.adjustment_date)}</td>
                  <td>ADJ-{r.id}</td>
                  <td>{r.tank_no || tankName(r.tank_id)}</td>
                  <td>{r.adjust_type === 'set' ? tr('stockAdjustment.set') : tr('stockAdjustment.change')}</td>
                  <td style={{ textAlign: 'right' }}>{fmtNum(r.qty)}</td>
                  <td style={{ textAlign: 'right' }}>{Number(r.rate) > 0 ? fmtNum(r.rate) : '—'}</td>
                  <td>{r.reason || '—'}</td>
                  <td>
                    <Can module="stock_ledger" action="delete">
                      <button className="btn btn-sm btn-danger" onClick={() => remove(r.id)}>{tr('common.delete')}</button>
                    </Can>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
